/* ============================================================
   QHCE 3D SHOWROOM — interactive PDP hero
   Studio scene · procedural valvetronic catback · X-ray mode
   virtual lift · hold-to-rev · tip configurator (variant-synced)
   All prefixed qhce3d-. Reduced-motion aware. No audio.
   ============================================================ */

(async function () {
  'use strict';

  const root = document.querySelector('[data-qhce3d]');
  if (!root || root.dataset.qhce3dInit) return;
  root.dataset.qhce3dInit = '1';

  const cfgEl = document.getElementById('qhce3d-config');
  let CFG = {};
  try { CFG = JSON.parse(cfgEl ? cfgEl.textContent : '{}') || {}; } catch (e) { CFG = {}; }

  const REDUCE = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const IS_TOUCH = matchMedia('(pointer: coarse)').matches;

  /* ---------- tuning (overridable from section settings JSON) ---------- */
  const EX_DEFAULTS = {       // procedural catback placement (car space)
    // tip pose measured off the F82 model's own tip geometry so ours shroud it exactly
    startZ: -0.62, tipZ: 2.30, pipeY: 0.25, pipeX: 0.135,
    axleZ: 1.38, axleRise: 0.16, muffZ: 1.92, muffY: 0.30,
    tipY: 0.297, tipXInner: 0.294, tipXOuter: 0.408, tipR: 0.0505
  };
  const TUNE = Object.assign({
    carLength: 4.67,          // normalize model to this length (m)
    carRotationY: Math.PI,    // extra yaw so nose faces -Z, tail +Z (F82 objaverse model ships +Z-nose)
    carLift: 0,               // manual y offset after floor-snap
    bodyMatNames: [],         // explicit body-paint material name hints
    camShowroom: { pos: [3.7, 1.45, 4.9], tgt: [0, 0.55, 0] },
    camRear: { pos: [1.7, 0.72, 4.55], tgt: [0, 0.42, 1.55] },
    camLift: { pos: [3.5, 1.05, 4.7], tgt: [0, 1.0, 0.75] },
    liftHeight: 0.98,
    bloom: 0.18
  }, CFG.tune || {});
  TUNE.exhaust = Object.assign({}, EX_DEFAULTS, (CFG.tune && CFG.tune.exhaust) || {});
  { // sanity clamps: auto-fitment can misread widebody kits/aero as tip clusters
    const ex = TUNE.exhaust;
    ex.tipXInner = Math.min(Math.max(ex.tipXInner, 0.18), 0.42);
    ex.tipXOuter = Math.min(Math.max(ex.tipXOuter, ex.tipXInner + 0.09), 0.56);
    ex.tipY = Math.min(Math.max(ex.tipY, 0.24), 0.48);
    ex.tipZ = Math.min(Math.max(ex.tipZ, 1.8), (TUNE.carLength || 4.67) / 2 + 0.06);
  }

  const MODEL_URL = CFG.modelUrl || '';
  const DRACO_PATH = 'https://www.gstatic.com/draco/versioned/decoders/1.5.7/';

  /* ---------- DOM refs ---------- */
  const $ = (s) => root.querySelector(s);
  const stage = $('.qhce3d-stage');
  const canvas = $('.qhce3d-canvas');
  const loaderEl = $('.qhce3d-loader');
  const loaderBar = $('.qhce3d-loader-fill');
  const loaderPct = $('.qhce3d-loader-pct');
  const hudEl = $('.qhce3d-hud');
  const hintEl = $('.qhce3d-hint');
  const dotLayer = $('.qhce3d-dots');
  const infoCard = $('.qhce3d-card');
  const rpmNum = $('.qhce3d-rpm-num');
  const barsEl = $('.qhce3d-bars');
  const fallbackEl = $('.qhce3d-fallback');

  function fail(msg) {
    try {
      if (loaderEl) loaderEl.style.display = 'none';
      if (hudEl) hudEl.style.display = 'none';
      if (fallbackEl) { fallbackEl.style.display = 'flex'; }
      console.warn('[qhce3d] fallback:', msg);
    } catch (e) {}
  }

  /* ---------- module loading (jsdelivr → esm.sh fallback) ---------- */
  const V = '0.169.0';
  const CDNS = [
    { base: `https://cdn.jsdelivr.net/npm/three@${V}`, suf: '/+esm' },
    { base: `https://esm.sh/three@${V}`, suf: '' }
  ];
  let THREE, OrbitControls, GLTFLoader, DRACOLoader, RoomEnvironment, EffectComposer, RenderPass, UnrealBloomPass, OutputPass, RGBELoader, Reflector;
  let loadedCdn = null;
  for (const cdn of CDNS) {
    try {
      const [core, orbit, gltf, draco, roomenv, composer, rpass, bloom, opass, rgbe, refl] = await Promise.all([
        import(`${cdn.base}${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/controls/OrbitControls.js${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/loaders/GLTFLoader.js${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/loaders/DRACOLoader.js${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/environments/RoomEnvironment.js${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/postprocessing/EffectComposer.js${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/postprocessing/RenderPass.js${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/postprocessing/UnrealBloomPass.js${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/postprocessing/OutputPass.js${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/loaders/RGBELoader.js${cdn.suf}`),
        import(`${cdn.base}/examples/jsm/objects/Reflector.js${cdn.suf}`)
      ]);
      THREE = core; OrbitControls = orbit.OrbitControls; GLTFLoader = gltf.GLTFLoader;
      DRACOLoader = draco.DRACOLoader; RoomEnvironment = roomenv.RoomEnvironment;
      EffectComposer = composer.EffectComposer; RenderPass = rpass.RenderPass;
      UnrealBloomPass = bloom.UnrealBloomPass; OutputPass = opass.OutputPass;
      RGBELoader = rgbe.RGBELoader; Reflector = refl.Reflector;
      loadedCdn = cdn.base;
      break;
    } catch (e) { console.warn('[qhce3d] cdn failed', cdn.base, e); }
  }
  if (!THREE) return fail('three.js failed to load');

  /* ---------- renderer ---------- */
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
  } catch (e) { return fail('WebGL unavailable'); }
  const QUALITY = { dpr: Math.min(devicePixelRatio || 1, IS_TOUCH ? 1.8 : 2), bloom: !IS_TOUCH, particles: !REDUCE };
  renderer.setPixelRatio(QUALITY.dpr);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.localClippingEnabled = true;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x050505);
  scene.fog = new THREE.FogExp2(0x050505, 0.052);

  const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 120);
  const BASE_FOV = 38;
  camera.position.set(0.4, 0.6, 9.5);

  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environmentIntensity = 0.32;
  async function upgradeEnv() {
    if (!CFG.hdrUrl) return;
    try {
      const tex = await new RGBELoader().loadAsync(CFG.hdrUrl);
      tex.mapping = THREE.EquirectangularReflectionMapping;
      const env = pmrem.fromEquirectangular(tex).texture;
      tex.dispose();
      scene.environment = env;
      scene.environmentIntensity = 0.45;
    } catch (e) { console.warn('[qhce3d] hdr env failed, keeping room env', e); }
  }

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.06;
  controls.maxPolarAngle = Math.PI * 0.495;
  controls.minDistance = 2.2;
  controls.maxDistance = 11;
  controls.target.set(0, 0.55, 0);
  controls.enablePan = false;
  controls.enableZoom = false;                 // page-scroll friendly; armed on interaction
  controls.autoRotate = !REDUCE;
  controls.autoRotateSpeed = 0.55;
  canvas.style.touchAction = 'pan-y';          // 1-finger vertical scroll passes through

  let idleTimer = null;
  function armIdle() {
    controls.autoRotate = false;
    clearTimeout(idleTimer);
    if (!REDUCE) idleTimer = setTimeout(() => { controls.autoRotate = true; }, 22000);
  }
  canvas.addEventListener('pointerdown', () => { controls.enableZoom = true; armIdle(); dismissHint(); });
  canvas.addEventListener('pointerleave', () => { controls.enableZoom = false; });
  canvas.addEventListener('wheel', () => { if (controls.enableZoom) armIdle(); }, { passive: true });

  let hintShown = true;
  function dismissHint() { if (hintShown && hintEl) { hintEl.classList.add('qhce3d-hide'); hintShown = false; } }

  /* ---------- tiny tween engine ---------- */
  const tweens = [];
  const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);
  const easeInOutCubic = (t) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  function animate(opts) {
    const tw = Object.assign({ t0: clockNow, dur: 1, ease: easeOutQuint }, opts);
    tweens.push(tw);
    return tw;
  }
  function stepTweens(now) {
    for (let i = tweens.length - 1; i >= 0; i--) {
      const tw = tweens[i];
      let k = (now - tw.t0) / tw.dur;
      if (k >= 1) k = 1;
      tw.update(tw.ease(k));
      if (k === 1) { tweens.splice(i, 1); if (tw.done) tw.done(); }
    }
  }
  let clockNow = 0;

  /* ---------- procedural textures ---------- */
  function carbonTexture() {
    const s = 256, c = document.createElement('canvas'); c.width = c.height = s;
    const g = c.getContext('2d');
    g.fillStyle = '#141414'; g.fillRect(0, 0, s, s);
    const cell = 16;
    for (let y = 0; y < s / cell; y++) {
      for (let x = 0; x < s / cell; x++) {
        const horiz = (x + y) % 2 === 0;
        const grad = g.createLinearGradient(x * cell, y * cell, horiz ? (x + 1) * cell : x * cell, horiz ? y * cell : (y + 1) * cell);
        grad.addColorStop(0, '#0b0b0b'); grad.addColorStop(0.5, '#2e2e2e'); grad.addColorStop(1, '#0b0b0b');
        g.fillStyle = grad;
        g.fillRect(x * cell, y * cell, cell, cell);
      }
    }
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(3, 1);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }
  function carbonSleeveTexture() {
    const size = 256, c = document.createElement('canvas'); c.width = size; c.height = size;
    const g = c.getContext('2d');
    g.fillStyle = '#141414'; g.fillRect(0, 0, size, size);
    const cell = 10;
    for (let y = 0; y < size / cell; y++) {
      for (let x = 0; x < size / cell; x++) {
        const horiz = (x + y) % 2 === 0;
        const grad = g.createLinearGradient(x * cell, y * cell, horiz ? (x + 1) * cell : x * cell, horiz ? y * cell : (y + 1) * cell);
        grad.addColorStop(0, '#070707'); grad.addColorStop(0.5, '#3a3a3a'); grad.addColorStop(1, '#070707');
        g.fillStyle = grad;
        g.fillRect(x * cell, y * cell, cell, cell);
      }
    }
    // polished slit strip (repeats around the sleeve)
    const sw = 12, sx = size / 2 - sw / 2;
    const sg = g.createLinearGradient(sx, 0, sx + sw, 0);
    sg.addColorStop(0, '#2e2e32'); sg.addColorStop(0.2, '#b9bac0'); sg.addColorStop(0.5, '#dddee4');
    sg.addColorStop(0.8, '#9fa0a6'); sg.addColorStop(1, '#2e2e32');
    g.fillStyle = sg;
    g.fillRect(sx, 0, sw, size);
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(3, 1);
    t.offset.x = 0.165;   // keep the polished slits off top-dead-center
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }
  function stripeTexture() {
    const c = document.createElement('canvas'); c.width = 256; c.height = 8;
    const g = c.getContext('2d');
    g.fillStyle = '#000'; g.fillRect(0, 0, 256, 8);
    g.fillStyle = '#fff';
    for (let x = 0; x < 256; x += 32) g.fillRect(x, 0, 12, 8);
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(6, 1);
    return t;
  }
  function glowSprite() {
    const s = 128, c = document.createElement('canvas'); c.width = c.height = s;
    const g = c.getContext('2d');
    const gr = g.createRadialGradient(s / 2, s / 2, 2, s / 2, s / 2, s / 2);
    gr.addColorStop(0, 'rgba(255,235,200,1)'); gr.addColorStop(0.25, 'rgba(255,120,50,0.85)');
    gr.addColorStop(0.6, 'rgba(200,16,46,0.35)'); gr.addColorStop(1, 'rgba(200,16,46,0)');
    g.fillStyle = gr; g.fillRect(0, 0, s, s);
    return new THREE.CanvasTexture(c);
  }
  function blobShadow() {
    const s = 256, c = document.createElement('canvas'); c.width = c.height = s;
    const g = c.getContext('2d');
    const gr = g.createRadialGradient(s / 2, s / 2, s * 0.05, s / 2, s / 2, s / 2);
    gr.addColorStop(0, 'rgba(0,0,0,0.85)'); gr.addColorStop(0.7, 'rgba(0,0,0,0.35)'); gr.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = gr; g.fillRect(0, 0, s, s);
    return new THREE.CanvasTexture(c);
  }

  function heatTintTexture() {
    const c = document.createElement('canvas'); c.width = 8; c.height = 128;
    const g = c.getContext('2d');
    const gr = g.createLinearGradient(0, 0, 0, 128);
    gr.addColorStop(0, '#b8b9be'); gr.addColorStop(0.3, '#c8a44a');
    gr.addColorStop(0.55, '#8a5a7a'); gr.addColorStop(0.78, '#4a6a9a'); gr.addColorStop(1, '#b0b1b6');
    g.fillStyle = gr; g.fillRect(0, 0, 8, 128);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }
  function flameTexture() {
    const c = document.createElement('canvas'); c.width = 64; c.height = 256;
    const g = c.getContext('2d');
    const gr = g.createLinearGradient(0, 256, 0, 0);   // base (v0) -> apex (v1)
    gr.addColorStop(0, 'rgba(255,252,235,0.95)');
    gr.addColorStop(0.22, 'rgba(255,214,110,0.9)');
    gr.addColorStop(0.5, 'rgba(255,120,30,0.7)');
    gr.addColorStop(0.8, 'rgba(200,40,16,0.3)');
    gr.addColorStop(1, 'rgba(160,16,16,0)');
    g.fillStyle = gr; g.fillRect(0, 0, 64, 256);
    return new THREE.CanvasTexture(c);
  }
  function etchTexture() {
    const c = document.createElement('canvas'); c.width = 512; c.height = 96;
    const g = c.getContext('2d');
    g.clearRect(0, 0, 512, 96);
    g.font = '600 44px Arial Narrow, Arial'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillStyle = 'rgba(235,235,240,0.92)';
    g.fillText('Q H C E', 256, 34);
    g.font = '600 17px Arial'; g.fillStyle = 'rgba(220,220,228,0.75)';
    g.fillText('V A L V E T R O N I C', 256, 72);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  /* ---------- studio ---------- */
  const studio = new THREE.Group();
  scene.add(studio);

  const floorMat = new THREE.MeshStandardMaterial({ color: 0x060606, metalness: 0.7, roughness: 0.42, envMapIntensity: 0.3 });
  const floor = new THREE.Mesh(new THREE.CircleGeometry(20, 72), floorMat);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  studio.add(floor);
  let mirror = null;
  if (!IS_TOUCH && Reflector) {
    try {
      mirror = new Reflector(new THREE.CircleGeometry(8.5, 64), {
        clipBias: 0.003, textureWidth: 1024, textureHeight: 1024, color: 0x9a9a9a
      });
      mirror.rotation.x = -Math.PI / 2;
      mirror.position.y = 0.001;
      studio.add(mirror);
      floor.position.y = 0.012;                 // translucent gloss over the mirror
      floorMat.transparent = true;
      floorMat.opacity = 0.86;
      floorMat.depthWrite = false;
    } catch (e) { console.warn('[qhce3d] reflector off', e); }
  }

  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(3.35, 0.009, 8, 128),
    new THREE.MeshBasicMaterial({ color: new THREE.Color(1.7, 1.7, 1.75), toneMapped: false })
  );
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.016;
  studio.add(ring);
  const ring2 = new THREE.Mesh(
    new THREE.TorusGeometry(3.55, 0.004, 8, 128),
    new THREE.MeshBasicMaterial({ color: new THREE.Color(1.4, 0.12, 0.28), toneMapped: false, transparent: true, opacity: 0.7 })
  );
  ring2.rotation.x = -Math.PI / 2; ring2.position.y = 0.015;
  studio.add(ring2);

  // soft blob contact shadow (cheap, always on)
  const blob = new THREE.Mesh(
    new THREE.PlaneGeometry(5.6, 2.6),
    new THREE.MeshBasicMaterial({ map: blobShadow(), transparent: true, opacity: 0.75, depthWrite: false })
  );
  blob.rotation.x = -Math.PI / 2; blob.position.y = 0.02;
  studio.add(blob);

  // (softbox strips removed — they bloomed into artifacts; ring + rim lights carry the look)

  /* lights */
  const hemi = new THREE.HemisphereLight(0x30343a, 0x050505, 0.5);
  scene.add(hemi);
  const key = new THREE.SpotLight(0xffffff, 0, 30, 0.9, 0.55, 1.4);
  key.position.set(4, 7.5, 3);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.bias = -0.0004; key.shadow.radius = 6;
  scene.add(key); scene.add(key.target);
  const rimRed = new THREE.SpotLight(0xc8102e, 0, 18, 0.36, 0.65, 2.0);
  rimRed.position.set(-6.4, 1.6, -7.0);
  rimRed.target.position.set(0, 0.72, -0.2);
  const rimCool = new THREE.SpotLight(0xbfd4ff, 0, 18, 0.55, 0.65, 1.7);
  rimCool.position.set(6.0, 2.0, -4.8);
  rimCool.target.position.set(0, 0.7, 0);
  [rimRed, rimCool].forEach(l => {
    l.castShadow = true;
    l.shadow.mapSize.set(1024, 1024);
    l.shadow.bias = -0.0005; l.shadow.radius = 4;
    scene.add(l); scene.add(l.target);
  });
  const underGlow = new THREE.PointLight(0xc8102e, 0, 6, 1.6);
  underGlow.position.set(0, 0.7, 1.3);
  scene.add(underGlow);
  const heatLight = new THREE.PointLight(0xff2d14, 0, 2.6, 2.0);
  heatLight.position.set(0, 0.34, 2.5);
  scene.add(heatLight);
  const LIGHT_FULL = { key: 380, rimRed: 280, rimCool: 120 };

  /* dust motes */
  let dust = null;
  if (QUALITY.particles) {
    const N = IS_TOUCH ? 130 : 300;
    const pos = new Float32Array(N * 3), seed = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      const r = 1.5 + Math.random() * 5.5, a = Math.random() * Math.PI * 2;
      pos[i * 3] = Math.cos(a) * r; pos[i * 3 + 1] = 0.1 + Math.random() * 3.4; pos[i * 3 + 2] = Math.sin(a) * r;
      seed[i] = Math.random() * 100;
    }
    const gg = new THREE.BufferGeometry();
    gg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    dust = new THREE.Points(gg, new THREE.PointsMaterial({ color: 0x8a8a92, size: 0.014, transparent: true, opacity: 0.4, depthWrite: false, sizeAttenuation: true }));
    dust.userData.seed = seed;
    scene.add(dust);
  }

  /* ---------- car group ---------- */
  const carGroup = new THREE.Group();  // car + exhaust, lifted together
  scene.add(carGroup);
  const carShell = new THREE.Group();  // just the body (ghosted in x-ray)
  carGroup.add(carShell);

  /* ---------- procedural QHCE catback ---------- */
  const EX = TUNE.exhaust;
  const exhaust = new THREE.Group();
  carGroup.add(exhaust);

  const carbonTex = carbonTexture();
  const MAT = {
    steel: new THREE.MeshPhysicalMaterial({ color: 0xd9dadf, metalness: 1, roughness: 0.24, envMapIntensity: 1.35 }),
    steelDark: new THREE.MeshPhysicalMaterial({ color: 0x9fa1a8, metalness: 1, roughness: 0.38, envMapIntensity: 1.0 }),
    carbon: new THREE.MeshPhysicalMaterial({ map: carbonSleeveTexture(), color: 0x4a4a4a, metalness: 0.05, roughness: 0.36, clearcoat: 1, clearcoatRoughness: 0.16, envMapIntensity: 0.36, anisotropy: 0.55 }),
    tipSteel: new THREE.MeshPhysicalMaterial({ color: 0xe3e4e8, metalness: 1, roughness: 0.14, envMapIntensity: 1.5, side: THREE.DoubleSide }),
    tipBlack: new THREE.MeshPhysicalMaterial({ color: 0x17171a, metalness: 1, roughness: 0.09, envMapIntensity: 1.6, side: THREE.DoubleSide }),
    tipGold: new THREE.MeshPhysicalMaterial({ color: 0xd8b23f, metalness: 1, roughness: 0.17, envMapIntensity: 1.5, side: THREE.DoubleSide }),
    actuator: new THREE.MeshStandardMaterial({ color: 0x2a2a2e, metalness: 0.7, roughness: 0.5 }),
    carbonEdge: new THREE.MeshStandardMaterial({ color: 0x1c1c1e, metalness: 0.4, roughness: 0.4 }),
    tipSteelBrushed: new THREE.MeshPhysicalMaterial({ color: 0xb6b7bc, metalness: 1, roughness: 0.34, envMapIntensity: 0.95, anisotropy: 0.7, side: THREE.DoubleSide }),
    boreSoot: new THREE.MeshStandardMaterial({ color: 0x0e0d0d, metalness: 0.35, roughness: 0.8, envMapIntensity: 0.35, side: THREE.BackSide }),
    boreSteel: new THREE.MeshStandardMaterial({ color: 0xa9aaaf, metalness: 1, roughness: 0.3, envMapIntensity: 0.9, side: THREE.BackSide })
  };

  function pipe(points, r) {
    const curve = new THREE.CatmullRomCurve3(points.map(p => new THREE.Vector3(...p)), false, 'catmullrom', 0.08);
    const geo = new THREE.TubeGeometry(curve, 72, r, 20, false);
    const m = new THREE.Mesh(geo, MAT.steel);
    m.castShadow = true;
    return { mesh: m, curve };
  }

  const flowMats = [];
  function flowOverlay(curve, r) {
    const geo = new THREE.TubeGeometry(curve, 72, r, 12, false);
    const tex = stripeTexture();
    const m = new THREE.MeshBasicMaterial({ color: 0xff3626, alphaMap: tex, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false });
    m.userData.tex = tex;
    flowMats.push(m);
    const mesh = new THREE.Mesh(geo, m);
    mesh.renderOrder = 5;
    return mesh;
  }

  const PIPE_R = 0.037;
  const weldMat = new THREE.MeshStandardMaterial({ color: 0x8f9095, metalness: 1, roughness: 0.52 });
  function weldBead(curve, t, r) {
    const bead = new THREE.Mesh(new THREE.TorusGeometry(r + 0.0035, 0.005, 8, 26), weldMat);
    const pt = curve.getPointAt(t), tan = curve.getTangentAt(t);
    bead.position.copy(pt);
    bead.lookAt(pt.clone().add(tan));
    exhaust.add(bead);
  }
  // ---- real F8X routing: front S-bends, X-crossover, wide spread into the muffler ends ----
  const mainPts = (sgn) => [                    // sgn=-1 starts left and crosses to the right bank
    [sgn * 0.09, EX.pipeY, EX.startZ],
    [sgn * 0.13, EX.pipeY, EX.startZ + 0.18],
    [sgn * 0.07, EX.pipeY + 0.008, EX.startZ + 0.38],
    [sgn * 0.05, EX.pipeY + (sgn < 0 ? 0.03 : -0.01), 0.05],
    [-sgn * 0.12, EX.pipeY + (sgn < 0 ? 0.028 : -0.008), 0.48],
    [-sgn * 0.30, EX.pipeY + 0.01, 0.95],
    [-sgn * 0.44, EX.pipeY + EX.axleRise, EX.axleZ],
    [-sgn * 0.46, EX.muffY + 0.02, EX.axleZ + 0.32],
    [-sgn * 0.40, EX.muffY, EX.muffZ - 0.12]
  ];
  const pipeL = pipe(mainPts(-1), PIPE_R);
  const pipeR = pipe(mainPts(1), PIPE_R);
  exhaust.add(pipeL.mesh, pipeR.mesh);
  exhaust.add(flowOverlay(pipeL.curve, PIPE_R + 0.006), flowOverlay(pipeR.curve, PIPE_R + 0.006));
  [pipeL, pipeR].forEach(pp => [0.07, 0.18, 0.3, 0.42, 0.55, 0.68, 0.8, 0.9].forEach(t => weldBead(pp.curve, t, PIPE_R)));

  // ribbed transverse muffler with rounded ends, QHCE plate on the underside
  const muff = new THREE.Mesh(new THREE.CapsuleGeometry(0.13, 0.75, 8, 22), MAT.steelDark);
  muff.rotation.z = Math.PI / 2;
  muff.position.set(0, EX.muffY, EX.muffZ);
  muff.castShadow = true;
  exhaust.add(muff);
  [-0.28, -0.095, 0.095, 0.28].forEach(rx => {
    const rib = new THREE.Mesh(new THREE.TorusGeometry(0.131, 0.0045, 8, 40), MAT.steelDark);
    rib.rotation.y = Math.PI / 2;
    rib.position.set(rx, EX.muffY, EX.muffZ);
    exhaust.add(rib);
  });

  const etch = new THREE.Mesh(new THREE.PlaneGeometry(0.34, 0.07),
    new THREE.MeshBasicMaterial({ map: etchTexture(), transparent: true, opacity: 0.85, depthWrite: false }));
  etch.position.set(0, EX.muffY - 0.133, EX.muffZ);
  etch.rotation.x = Math.PI / 2;
  etch.renderOrder = 3;
  exhaust.add(etch);

  // hanger rods
  [-1, 1].forEach(sgn => {
    const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.007, 0.007, 0.24, 10), MAT.steel);
    rod.rotation.z = Math.PI / 2;
    rod.position.set(sgn * 0.34, EX.pipeY + 0.05, 1.28);
    exhaust.add(rod);
  });

  // valve bodies + black actuators on the muffler ends (like the real unit)
  const valveLeds = [];
  [-1, 1].forEach(sgn => {
    const vg = new THREE.Group();
    const housing = new THREE.Mesh(new THREE.CylinderGeometry(0.047, 0.047, 0.085, 18), MAT.steelDark);
    housing.rotation.z = Math.PI / 2;
    const act = new THREE.Mesh(new THREE.BoxGeometry(0.085, 0.05, 0.095), MAT.actuator);
    act.position.set(0, 0.07, -0.015);
    const led = new THREE.Mesh(new THREE.SphereGeometry(0.009, 8, 8), new THREE.MeshBasicMaterial({ color: 0x330000, toneMapped: false }));
    led.position.set(sgn * 0.03, 0.098, -0.015);
    valveLeds.push(led.material);
    vg.add(housing, act, led);
    vg.position.set(sgn * 0.52, EX.muffY, EX.muffZ);
    exhaust.add(vg);
  });

  // per-side outlet: valve -> elbow -> Y -> both tip entries (the exact points the tips expect)
  [-1, 1].forEach(sgn => {
    const main = pipe([
      [sgn * 0.52, EX.muffY, EX.muffZ + 0.05],
      [sgn * 0.47, EX.muffY - 0.01, EX.muffZ + 0.10],
      [sgn * 0.40, EX.tipY + 0.004, EX.muffZ + 0.14]
    ], 0.035);
    exhaust.add(main.mesh);
    weldBead(main.curve, 0.5, 0.035);
    ((TUNE.tipCount === 3) ? [0.115, 0.012] : (TUNE.tipCount === 2) ? [(EX.tipXInner + EX.tipXOuter) / 2] : [EX.tipXInner, EX.tipXOuter]).forEach(tx => {
      const br = pipe([
        [sgn * 0.40, EX.tipY + 0.004, EX.muffZ + 0.14],
        [sgn * (0.40 + tx) / 2, EX.tipY + 0.002, (EX.muffZ + 0.16 + EX.tipZ - 0.155) / 2],
        [sgn * tx, EX.tipY, EX.tipZ - 0.155]
      ], 0.031);
      exhaust.add(br.mesh);
    });
  });

  // quad tips
  const tips = [];          // { group, sleeve, inner, lip, glowMat }
  const TIP_COUNT = [0, 2, 3].includes(TUNE.tipCount) ? TUNE.tipCount : 4;
  const tipXs = TIP_COUNT === 2
    ? [-(EX.tipXInner + EX.tipXOuter) / 2, (EX.tipXInner + EX.tipXOuter) / 2]
    : TIP_COUNT === 3
      ? [-0.115, 0, 0.115]              // Type-R style center triple
      : [-EX.tipXOuter, -EX.tipXInner, EX.tipXInner, EX.tipXOuter];
  tipXs.forEach((x, i) => {
    const g = new THREE.Group();
    if (TIP_COUNT === 0) {
      // sold without tips: bare pipe ends with a rolled edge, tucked at the bumper line
      const pipeEnd = new THREE.Mesh(new THREE.CylinderGeometry(0.0335, 0.0335, 0.1, 20, 1, true), MAT.tipSteel);
      pipeEnd.rotation.x = Math.PI / 2; pipeEnd.position.z = -0.14;
      const roll = new THREE.Mesh(new THREE.TorusGeometry(0.0335, 0.003, 8, 24), MAT.tipSteel);
      roll.position.z = -0.09;
      const glowMat0 = new THREE.MeshBasicMaterial({ color: 0x050202, toneMapped: false });
      const glow0 = new THREE.Mesh(new THREE.CircleGeometry(0.03, 18), glowMat0);
      glow0.position.z = -0.1;
      const soot0 = new THREE.Mesh(new THREE.CylinderGeometry(0.0325, 0.0325, 0.09, 18, 1, true),
        new THREE.MeshStandardMaterial({ color: 0x0e0d0d, metalness: 0.35, roughness: 0.8, envMapIntensity: 0.35, side: THREE.BackSide }));
      soot0.rotation.x = Math.PI / 2; soot0.position.z = -0.14;
      g.add(pipeEnd, roll, soot0, glow0);
      g.position.set(x, EX.tipY, EX.tipZ - 0.02);
      exhaust.add(g);
      tips.push({ group: g, sleeve: pipeEnd, inner: pipeEnd, lip: roll, glowMat: glowMat0 });
      return;
    }
    // fed by the per-side Y-plumbing above; the tip assembly itself is untouched

    // polished reducer: pipe -> liner diameter (stays visible behind the sleeve)
    const reducer = new THREE.Mesh(new THREE.CylinderGeometry(0.0445, 0.031, 0.065, 24), MAT.tipSteel);
    reducer.rotation.x = Math.PI / 2;
    reducer.position.z = -0.1675;
    // burnt heat tint where the weld meets the pipe
    const tint = new THREE.Mesh(new THREE.CylinderGeometry(0.0325, 0.0325, 0.045, 20, 1, true),
      new THREE.MeshStandardMaterial({ map: heatTintTexture(), metalness: 1, roughness: 0.34, envMapIntensity: 1.0 }));
    tint.rotation.x = Math.PI / 2; tint.position.z = -0.205;

    // steel liner: runs the length of the sleeve and pokes a few mm past the carbon edge
    const inner = new THREE.Mesh(new THREE.CylinderGeometry(0.0445, 0.0445, 0.165, 26, 1, true), MAT.tipSteel);
    inner.rotation.x = Math.PI / 2; inner.position.z = -0.0305;
    const lip = new THREE.Mesh(new THREE.TorusGeometry(0.0455, 0.0035, 12, 32), MAT.tipSteel);
    lip.position.z = 0.052;

    // carbon (or steel) sleeve over the liner — front edge stops just short of the lip
    const sleeve = new THREE.Mesh(new THREE.CylinderGeometry(0.051, 0.051, 0.16, 30, 1, true), MAT.carbon);
    sleeve.rotation.x = Math.PI / 2; sleeve.position.z = -0.035;
    const sleeveEdge = new THREE.Mesh(new THREE.TorusGeometry(0.051, 0.0018, 8, 30), MAT.carbonEdge);
    sleeveEdge.position.z = 0.045;
    // closed rear annulus so you can never see through the barrel
    const backCap = new THREE.Mesh(new THREE.RingGeometry(0.0325, 0.051, 30), MAT.steelDark);
    backCap.position.z = -0.104;
    backCap.rotation.y = Math.PI;

    // soot bore: chrome ends just past the lip, then the pipe goes carbon-black inside
    const soot = new THREE.Mesh(new THREE.CylinderGeometry(0.0435, 0.0435, 0.147, 26, 1, true), MAT.boreSoot);
    soot.rotation.x = Math.PI / 2; soot.position.z = -0.0275;

    // recessed gas glow: black at idle, ignites on rev
    const glowMat = new THREE.MeshBasicMaterial({ color: 0x050202, toneMapped: false });
    const glow = new THREE.Mesh(new THREE.CircleGeometry(0.041, 26), glowMat);
    glow.position.z = -0.055;

    g.add(reducer, tint, inner, lip, sleeve, sleeveEdge, backCap, soot, glow);
    g.position.set(x, EX.tipY, EX.tipZ);
    g.rotation.y = (x > 0 ? 1 : -1) * 0.015;
    exhaust.add(g);
    tips.push({ group: g, sleeve, inner, lip, sleeveEdge, soot, glowMat });
  });

  // flame jets: one unit per tip, hidden until a burble ignites
  const flameTex = flameTexture();
  let heatFlash = 0;
  tips.forEach(t => {
    const mkCone = (r, len, op) => new THREE.Mesh(
      new THREE.ConeGeometry(r, len, 12, 1, true),
      new THREE.MeshBasicMaterial({ map: flameTex, transparent: true, opacity: op, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false })
    );
    const outer = mkCone(0.043, 0.30, 0);
    const core = mkCone(0.024, 0.20, 0);
    [outer, core].forEach(m => { m.rotation.x = Math.PI / 2; m.visible = false; t.group.add(m); });
    outer.position.z = 0.20; core.position.z = 0.15;
    t.flame = { outer, core, life: 0, max: 0, power: 1 };
  });
  function spawnFlame(t, power) {
    t.flame.life = t.flame.max = 0.15 + Math.random() * 0.1 + power * 0.1;
    t.flame.power = power;
    heatFlash = Math.min(1, heatFlash + 0.55 + power * 0.3);
  }

  // crackle sprites pool
  const sprTex = glowSprite();
  const sprites = [];
  for (let i = 0; i < 24; i++) {
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: sprTex, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }));
    sp.scale.setScalar(0.001);
    sp.visible = false;
    carGroup.add(sp);
    sprites.push({ sp, life: 0, max: 0.16 });
  }
  let sprIdx = 0;
  function popAt(tip, big, flame) {
    if (state.sound) engineAudio.pop(big, flame, 0.5 + state.burble / 160);
    const s = sprites[sprIdx++ % sprites.length];
    const wp = new THREE.Vector3();
    tip.group.getWorldPosition(wp);
    carGroup.worldToLocal(wp);
    s.sp.position.copy(wp).add(new THREE.Vector3((Math.random() - 0.5) * 0.03, (Math.random() - 0.5) * 0.03, 0.09 + Math.random() * 0.05));
    s.life = s.max = big ? 0.16 + Math.random() * 0.08 : 0.09 + Math.random() * 0.05;
    s.big = big;
    s.sp.visible = true;
  }

  /* ---------- engine audio (WebAudio synth, opt-in, default OFF) ---------- */
  const ENGINE_WORKLET = `
class QhceEngine extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [
      { name: 'freq', defaultValue: 47.5, minValue: 5, maxValue: 900, automationRate: 'k-rate' },
      { name: 'tension', defaultValue: 0, minValue: 0, maxValue: 1, automationRate: 'k-rate' }
    ];
  }
  constructor() {
    super();
    this.phase = 0; this.env = 0; this.amp = 0; this.cyl = 0;
    this.lp = 0; this.wob = 0;
  }
  process(inputs, outputs, params) {
    const out = outputs[0][0];
    const f = params.freq[0], tension = params.tension[0];
    const dt = 1 / sampleRate;
    const dec = Math.exp(-dt * (600 + f * 14));   // pulses stay distinct as rpm climbs
    for (let i = 0; i < out.length; i++) {
      this.phase += f * dt;
      if (this.phase >= 1) {
        this.phase -= 1;
        this.cyl = (this.cyl + 1) % 6;
        const jit = 0.72 + Math.random() * 0.56;             // combustion variation
        const halfOrder = (this.cyl % 2 === 0) ? 1.18 : 0.86; // I6 exhaust unevenness = rumble
        const lope = 1 + (f < 80 ? 0.25 : 0.06) * Math.sin(this.wob += 0.9 + Math.random() * 0.4);
        this.amp = jit * halfOrder * lope;
        this.env = 1;
      }
      this.env *= dec;
      const noise = Math.random() * 2 - 1;
      const s = this.amp * this.env * (0.55 + (0.3 + 0.45 * tension) * noise);
      const k = 0.12 + 0.25 * tension;              // pulse warmth opens with throttle
      this.lp += k * (s - this.lp);
      out[i] = this.lp * 2.6;
    }
    return true;
  }
}
registerProcessor('qhce-engine', QhceEngine);
`;

  const engineAudio = (() => {
    let ctx = null, n = null, building = null;
    async function build() {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const master = ctx.createGain(); master.gain.value = 0;
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -8; comp.ratio.value = 3;
      master.connect(comp); comp.connect(ctx.destination);

      // cylinder firing stack: saw fundamental + sub + harmonic, distorted
      const shaper = ctx.createWaveShaper();
      const curve = new Float32Array(512);
      for (let i = 0; i < 512; i++) { const v = (i / 255.5) - 1; curve[i] = Math.tanh(4.5 * v); }
      shaper.curve = curve;
      const mk = (type, gain) => {
        const o = ctx.createOscillator(); o.type = type;
        const g = ctx.createGain(); g.gain.value = gain;
        o.connect(g); g.connect(shaper); o.start();
        return o;
      };
      const oF = mk('sawtooth', 0.2);    // tonal bed under the pulse train
      const oS = mk('square', 0.16);
      const oH = mk('sawtooth', 0.1);
      const oH2 = mk('sawtooth', 0.08);

      // combustion pulse train (the actual engine voice)
      let pulse = null;
      try {
        const wurl = URL.createObjectURL(new Blob([ENGINE_WORKLET], { type: 'text/javascript' }));
        await ctx.audioWorklet.addModule(wurl);
        pulse = new AudioWorkletNode(ctx, 'qhce-engine', { numberOfInputs: 0, outputChannelCount: [1] });
        const pgain = ctx.createGain(); pgain.gain.value = 0.9;
        pulse.connect(pgain); pgain.connect(shaper);
      } catch (e) { console.warn('[qhce3d] worklet unavailable, tonal-only voice', e); }

      // body resonances + valve-controlled muffling
      const bp = ctx.createBiquadFilter(); bp.type = 'peaking'; bp.frequency.value = 220; bp.Q.value = 1.1; bp.gain.value = 9;
      const bp2 = ctx.createBiquadFilter(); bp2.type = 'peaking'; bp2.frequency.value = 950; bp2.Q.value = 1.0; bp2.gain.value = 5;
      const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 900; lp.Q.value = 0.7;
      const duck = ctx.createGain();   // pops sidechain-duck the engine bed through this
      shaper.connect(bp); bp.connect(bp2); bp2.connect(lp); lp.connect(duck); duck.connect(master);

      // exhaust rasp: noise amplitude-modulated at the firing rate
      const nb = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
      const nd = nb.getChannelData(0);
      for (let i = 0; i < nd.length; i++) nd[i] = Math.random() * 2 - 1;
      const noise = ctx.createBufferSource(); noise.buffer = nb; noise.loop = true;
      const nf = ctx.createBiquadFilter(); nf.type = 'bandpass'; nf.frequency.value = 950; nf.Q.value = 0.8;
      const ng = ctx.createGain(); ng.gain.value = 0.035;
      const am = ctx.createOscillator(); am.type = 'square';
      const amg = ctx.createGain(); amg.gain.value = 0.018;
      am.connect(amg); amg.connect(ng.gain);
      noise.connect(nf); nf.connect(ng); ng.connect(lp);  // lp feeds duck -> master
      const tf = ctx.createBiquadFilter(); tf.type = 'bandpass'; tf.frequency.value = 3400; tf.Q.value = 1.6;
      const tg = ctx.createGain(); tg.gain.value = 0;
      noise.connect(tf); tf.connect(tg); tg.connect(duck);
      noise.start(); am.start();

      // output: makeup -> tanh soft-clip limiter (no digital clipping) + analyser tap for probe()
      const makeup = ctx.createGain(); makeup.gain.value = 1.15;
      const limiter = ctx.createWaveShaper();
      const lc = new Float32Array(1024);
      for (let i = 0; i < 1024; i++) { const v = (i / 511.5) - 1; lc[i] = Math.tanh(1.6 * v) / Math.tanh(1.6); }
      limiter.curve = lc;
      const analyser = ctx.createAnalyser(); analyser.fftSize = 2048;
      comp.disconnect();
      comp.connect(makeup); makeup.connect(limiter); limiter.connect(ctx.destination); limiter.connect(analyser);

      n = { master, comp, oF, oS, oH, oH2, lp, bp, bp2, ng, am, duck, analyser, pulse, tg };
    }
    let updTimer = null;
    const api = {
      start() {
        if (!building) building = build();
        building.then(() => ctx.resume());
        // audio follows state on its own clock — never depends on the render loop
        if (!updTimer) updTimer = setInterval(() => api.update(state.rpm, state.throttle, state.valves, state.sound), 50);
      },
      stop() {
        if (ctx) ctx.suspend();
        if (updTimer) { clearInterval(updTimer); updTimer = null; }
      },
      probe() {
        if (!ctx || !n) return { state: ctx ? ctx.state : 'none' };
        const buf = new Float32Array(n.analyser.fftSize);
        n.analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        return { state: ctx.state, masterGain: +n.master.gain.value.toFixed(4), rms: +Math.sqrt(sum / buf.length).toFixed(5) };
      },
      update(rpm, throttle, valves, on) {
        if (!ctx || !n || ctx.state !== 'running') return;
        const t = ctx.currentTime;
        const f = (rpm / 60) * (CYL / 2);               // four-stroke firing rate for this engine
        const rf = Math.max(0, (rpm - 950) / 7050);
        n.oF.frequency.setTargetAtTime(f, t, 0.02);
        n.oS.frequency.setTargetAtTime(f * 0.5, t, 0.02);
        n.oH.frequency.setTargetAtTime(f * 2.013, t, 0.02);
        n.oH2.frequency.setTargetAtTime(f * 3.02, t, 0.02);
        if (n.pulse) {
          n.pulse.parameters.get('freq').setTargetAtTime(f, t, 0.02);
          n.pulse.parameters.get('tension').setTargetAtTime(Math.min(1, throttle * 0.75 + rf * 0.35), t, 0.05);
        }
        n.tg.gain.setTargetAtTime(rf * throttle * 0.05, t, 0.08);
        n.am.frequency.setTargetAtTime(f, t, 0.02);
        n.lp.frequency.setTargetAtTime(valves ? (1700 + rf * 4600) : (800 + rf * 1100), t, 0.05);
        n.bp.frequency.setTargetAtTime(180 + rf * 320, t, 0.05);
        n.ng.gain.setTargetAtTime((valves ? 0.08 : 0.025) + rf * (valves ? 0.14 : 0.04), t, 0.05);
        const vol = on ? ((0.12 + rf * 0.12 + throttle * 0.5) * (valves ? 1.25 : 0.7)) : 0;
        n.master.gain.setTargetAtTime(vol, t, 0.06);
      },
      pop(big, flame, vol) {
        if (!ctx || !n || ctx.state !== 'running') return;
        const t = ctx.currentTime;
        const dur = flame ? 0.26 : (big ? 0.17 : 0.09);
        const src = ctx.createBufferSource();
        const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / d.length, 1.6);
        src.buffer = buf;
        const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.Q.value = 1.1;
        f.frequency.setValueAtTime(flame ? 340 : (big ? 520 : 800), t);
        f.frequency.exponentialRampToValueAtTime(flame ? 95 : 180, t + dur);
        const g = ctx.createGain(); g.gain.value = (flame ? 26 : (big ? 20 : 12)) * (vol || 1);
        src.connect(f); f.connect(g); g.connect(n.comp);
        src.start(t); src.stop(t + dur);
        // sidechain: dip the engine bed so the crack punches through
        n.duck.gain.cancelScheduledValues(t);
        n.duck.gain.setValueAtTime(flame ? 0.22 : 0.32, t);
        n.duck.gain.setTargetAtTime(1, t + 0.045, 0.09);
        if (flame) {
          const o = ctx.createOscillator(); o.type = 'sine';
          o.frequency.setValueAtTime(74, t);
          o.frequency.exponentialRampToValueAtTime(42, t + 0.22);
          const og = ctx.createGain();
          og.gain.setValueAtTime(0.9 * (vol || 1), t);
          og.gain.exponentialRampToValueAtTime(0.001, t + 0.24);
          o.connect(og); og.connect(n.comp);
          o.start(t); o.stop(t + 0.25);
        }
      }
    };
    return api;
  })();

  /* ---------- state ---------- */
  const state = {
    view: 'showroom', xray: false, valves: false, throttle: 0, rpm: 950,
    tip: (variantKeyFromText(CFG.initialTip || '') || 'cf-steel'), paint: 'yas', lifted: 0, loaded: false, shake: 0, sound: false, flames: false, burble: 40, fit: 0
  };
  const IDLE = 950, REDLINE = (TUNE.redline || 7000), MAXR = REDLINE + 1000, CYL = (TUNE.cyl || 6);

  /* ---------- car loading ---------- */
  let bodyMats = [];          // repaintable materials
  let factoryColors = [];     // original body colors
  let ghostCar = null;
  const ghostMats = [];
  let origMatMeshes = [];     // [mesh, material] for clip control

  function ghostMaterial() {
    const m = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0x86a8c8) },
        uAlpha: { value: 0.0 },
        uClipZ: { value: -10 }, uClipOn: { value: 0 }
      },
      vertexShader: `
        varying vec3 vN; varying vec3 vV; varying vec3 vW;
        void main(){
          vec4 mv = modelViewMatrix * vec4(position,1.0);
          vN = normalize(normalMatrix * normal);
          vV = normalize(-mv.xyz);
          vW = (modelMatrix * vec4(position,1.0)).xyz;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        uniform vec3 uColor; uniform float uAlpha; uniform float uClipZ; uniform float uClipOn;
        varying vec3 vN; varying vec3 vV; varying vec3 vW;
        void main(){
          if (uClipOn > 0.5 && vW.z > uClipZ) discard;
          vec3 Nn = normalize(vN + vec3(1e-7));
          vec3 Vn = normalize(vV + vec3(1e-7));
          float d = clamp(abs(dot(Nn, Vn)), 0.0, 1.0);
          float f = pow(clamp(1.0 - d, 0.0, 1.0), 2.6);
          float scan = 0.94 + 0.06 * sin(vW.y * 90.0);
          vec3 col = uColor * (0.16 + 1.5 * f) * scan;
          gl_FragColor = vec4(col, uAlpha * (0.028 + 0.72 * f));
        }`,
      transparent: true, depthWrite: false, side: THREE.DoubleSide
    });
    ghostMats.push(m);
    return m;
  }

  const clipPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 100); // keeps z > clipZ when constant = -clipZ

  /* name-keyed PBR upgrades (SketchUp-converted models ship flat materials) */
  const upgraded = new Map();
  function upgradeMaterial(mt) {
    if (!mt) return mt;
    if (upgraded.has(mt)) return upgraded.get(mt);
    let out = mt;
    const nm = (mt.name || '').toLowerCase();
    const isBody = /^body/.test(nm) || (TUNE.bodyMatNames || []).some(h => nm.includes(h.toLowerCase()));
    if (isBody) {
      out = new THREE.MeshPhysicalMaterial({
        name: mt.name, color: mt.color ? mt.color.clone() : new THREE.Color(0x3672b9),
        metalness: 0.72, roughness: 0.34, clearcoat: 1, clearcoatRoughness: 0.06, envMapIntensity: 1.15
      });
    } else if (/chrome|silver/.test(nm)) {
      mt.metalness = 1; mt.roughness = 0.22; mt.envMapIntensity = 1.3;
    } else if (/r_glass/.test(nm)) {
      mt.transparent = true; mt.opacity = 0.75; mt.roughness = 0.1; mt.metalness = 0.2;
      if (mt.emissive) mt.emissive.setHex(0x230204);
      mt.envMapIntensity = 1.6; mt.depthWrite = false;
    } else if (/glass/.test(nm)) {
      mt.transparent = true; mt.opacity = 0.3; mt.roughness = 0.05; mt.metalness = 0.1;
      mt.envMapIntensity = 1.9; mt.depthWrite = false;
    } else if (/caliper/.test(nm)) {
      if (mt.color) mt.color.setHex(0xc8102e);
      mt.metalness = 0.45; mt.roughness = 0.38;
    } else if (/light/.test(nm)) {
      mt.metalness = 0.9; mt.roughness = 0.12; mt.envMapIntensity = 2.0;
    } else if (/leather|charcoal/.test(nm)) {
      mt.roughness = 0.85; mt.envMapIntensity = 0.4;
    } else if (/black1|^material\d/.test(nm)) {
      mt.roughness = 0.72; mt.envMapIntensity = 0.45;
    } else if (mt.metalness !== undefined && mt.roughness !== undefined && mt.roughness >= 0.55 && mt.metalness === 0) {
      mt.roughness = 0.5; // generic flat → slightly tightened
    }
    upgraded.set(mt, out);
    return out;
  }

  function prepCar(carRoot) {
    // normalize: scale to carLength, ground on floor, center
    const box = new THREE.Box3().setFromObject(carRoot);
    const size = box.getSize(new THREE.Vector3());
    const len = Math.max(size.x, size.z);
    const scale = TUNE.carLength / len;
    carRoot.scale.setScalar(scale);
    if (size.x > size.z) carRoot.rotation.y = Math.PI / 2; // length was along x → rotate to z
    carRoot.rotation.y += TUNE.carRotationY;
    const box2 = new THREE.Box3().setFromObject(carRoot);
    const c = box2.getCenter(new THREE.Vector3());
    carRoot.position.x -= c.x;
    carRoot.position.z -= c.z;
    carRoot.position.y -= box2.min.y;
    carRoot.position.y += TUNE.carLift;

    // material pass: upgrade to PBR by name, then detect the paint material
    const areas = new Map();
    carRoot.traverse((o) => {
      if (!o.isMesh) return;
      o.castShadow = true;
      o.receiveShadow = false;
      if (Array.isArray(o.material)) o.material = o.material.map(upgradeMaterial);
      else o.material = upgradeMaterial(o.material);
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((mt) => {
        if (!mt) return;
        origMatMeshes.push([o, mt]);
        // candidate body material: named hint or opaque colored standard material
        const nm = (mt.name || '').toLowerCase();
        const hinted = /^body/.test(nm) || (TUNE.bodyMatNames || []).some(h => nm.includes(h.toLowerCase()));
        if (hinted) { areas.set(mt, 1e9); return; }
        if (mt.transparent || (mt.opacity !== undefined && mt.opacity < 0.95)) return;
        if (/glass|window|light|lamp|tire|tyre|rubber|chrome|mirror|interior|plate/.test(nm)) return;
        const g = o.geometry;
        let area = 0;
        if (g && g.attributes && g.attributes.position) area = g.attributes.position.count;
        areas.set(mt, (areas.get(mt) || 0) + area);
      });
    });
    const sorted = [...areas.entries()].sort((a, b) => b[1] - a[1]);
    bodyMats = sorted.slice(0, 1).map(e => e[0]);
    // include materials sharing (nearly) the same color as the top mat (multi-part bodies)
    if (bodyMats[0] && bodyMats[0].color) {
      const c0 = bodyMats[0].color;
      sorted.slice(1, 6).forEach(([mt]) => {
        if (mt.color && Math.abs(mt.color.r - c0.r) + Math.abs(mt.color.g - c0.g) + Math.abs(mt.color.b - c0.b) < 0.08) bodyMats.push(mt);
      });
    }
    factoryColors = bodyMats.map(mt => mt.color ? mt.color.clone() : null);
    bodyMats.forEach(mt => {
      if (mt.clearcoat !== undefined) { mt.clearcoat = Math.max(mt.clearcoat || 0, 0.6); mt.clearcoatRoughness = 0.12; }
    });

    // the F82 mesh ships its own exhaust tips baked into black1/silver1 —
    // carve those triangles out so only the QHCE assembly shows at any fitment.
    // (zones validated offline: 2390 black1 + 974 silver1 tris, 0 body1 touched)
    carRoot.updateMatrixWorld(true);
    const tv = new THREE.Vector3();
    carRoot.traverse((o) => {
      if (!o.isMesh || !o.geometry || !o.geometry.index) return;
      const mnm = ((Array.isArray(o.material) ? o.material[0] : o.material).name || '').toLowerCase();
      if (!/black1|silver1/.test(mnm)) return;
      const posA = o.geometry.attributes.position, idx = o.geometry.index;
      const keep = [];
      for (let i = 0; i < idx.count; i += 3) {
        let cx = 0, cy = 0, cz = 0;
        for (let k = 0; k < 3; k++) {
          tv.fromBufferAttribute(posA, idx.getX(i + k)).applyMatrix4(o.matrixWorld);
          cx += tv.x / 3; cy += tv.y / 3; cz += tv.z / 3;
        }
        const hit = cy > 0.235 && cy < 0.355 && cz > 2.13 && cz < 2.35 && Math.abs(cx) > 0.22 && Math.abs(cx) < 0.48;
        if (!hit) keep.push(idx.getX(i), idx.getX(i + 1), idx.getX(i + 2));
      }
      if (keep.length < idx.count) o.geometry.setIndex(keep);
    });

    carShell.add(carRoot);

    // ghost twin (shared geometry)
    ghostCar = carRoot.clone(true);
    ghostCar.traverse((o) => { if (o.isMesh) { o.material = ghostMaterial(); o.castShadow = true; o.renderOrder = 4; } });
    ghostCar.visible = false;
    carShell.add(ghostCar);
  }

  function buildSilhouette() {
    // stylized F8X coupe silhouette (fallback / no-model mode)
    const pts = [
      [-2.30, 0.30], [-2.33, 0.52], [-2.20, 0.62], [-1.60, 0.70], [-1.05, 0.75],
      [-0.85, 0.80], [-0.30, 1.13], [0.42, 1.19], [1.05, 1.11], [1.60, 0.93],
      [2.05, 0.89], [2.28, 0.84], [2.33, 0.62], [2.30, 0.34], [2.10, 0.20],
      [-2.10, 0.20]
    ];
    const shape = new THREE.Shape();
    shape.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) shape.lineTo(pts[i][0], pts[i][1]);
    shape.closePath();
    const geo = new THREE.ExtrudeGeometry(shape, { depth: 1.72, bevelEnabled: true, bevelThickness: 0.09, bevelSize: 0.09, bevelSegments: 3, steps: 1 });
    const mat = new THREE.MeshPhysicalMaterial({ color: 0x121216, metalness: 0.9, roughness: 0.36, envMapIntensity: 0.9, clearcoat: 0.7, clearcoatRoughness: 0.2 });
    const body = new THREE.Mesh(geo, mat);
    body.castShadow = true;
    const g = new THREE.Group();
    body.rotation.y = -Math.PI / 2;
    body.position.x = 0.95; // center the extrude width
    g.add(body);
    // wheels
    const tyre = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.9 });
    const rim = new THREE.MeshPhysicalMaterial({ color: 0xb8b8bd, metalness: 1, roughness: 0.25 });
    [[-1.4, -0.83], [-1.4, 0.83], [1.41, -0.83], [1.41, 0.83]].forEach(([z, x]) => {
      const w = new THREE.Mesh(new THREE.CylinderGeometry(0.345, 0.345, 0.26, 28), tyre);
      w.rotation.z = Math.PI / 2; w.position.set(x, 0.345, z); w.castShadow = true;
      const r = new THREE.Mesh(new THREE.CylinderGeometry(0.20, 0.20, 0.27, 20), rim);
      r.rotation.z = Math.PI / 2; r.position.copy(w.position);
      g.add(w, r);
    });
    bodyMats = [mat];
    factoryColors = [mat.color.clone()];
    carShell.add(g);
    // ghost twin
    ghostCar = g.clone(true);
    ghostCar.traverse((o) => { if (o.isMesh) { o.material = ghostMaterial(); o.castShadow = true; o.renderOrder = 4; } });
    ghostCar.visible = false;
    carShell.add(ghostCar);
    origMatMeshes = [];
    g.traverse((o) => { if (o.isMesh) origMatMeshes.push([o, o.material]); });
  }

  function loadCar() {
    return new Promise((resolve) => {
      if (!MODEL_URL) { buildSilhouette(); resolve(false); return; }
      const loader = new GLTFLoader();
      try {
        const dl = new DRACOLoader();
        dl.setDecoderPath(DRACO_PATH);
        loader.setDRACOLoader(dl);
      } catch (e) {}
      loader.load(MODEL_URL, (gltf) => {
        try { prepCar(gltf.scene); resolve(true); }
        catch (e) { console.warn('[qhce3d] prep failed', e); buildSilhouette(); resolve(false); }
      }, (ev) => {
        if (ev.lengthComputable && loaderBar) {
          const k = Math.min(1, ev.loaded / ev.total);
          loaderBar.style.transform = `scaleX(${k})`;
          if (loaderPct) loaderPct.textContent = Math.round(k * 100) + '%';
        }
      }, (err) => {
        console.warn('[qhce3d] model load failed', err);
        buildSilhouette(); resolve(false);
      });
    });
  }

  /* ---------- post ---------- */
  let composer = null, bloomPass = null;
  function setupPost() {
    if (!QUALITY.bloom) return;
    try {
      const sz = renderer.getDrawingBufferSize(new THREE.Vector2());
      const rt = new THREE.WebGLRenderTarget(sz.width, sz.height, { samples: 4, type: THREE.HalfFloatType });
      composer = new EffectComposer(renderer, rt);
      composer.addPass(new RenderPass(scene, camera));
      bloomPass = new UnrealBloomPass(new THREE.Vector2(1, 1), TUNE.bloom, 0.3, 0.92);
      composer.addPass(bloomPass);
      composer.addPass(new OutputPass());
    } catch (e) { composer = null; console.warn('[qhce3d] bloom off', e); }
  }

  /* ---------- sizing ---------- */
  function resize() {
    const w = stage.clientWidth, h = stage.clientHeight;
    if (!w || !h) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
    if (composer) composer.setSize(w, h);
  }
  new ResizeObserver(resize).observe(stage);

  /* ---------- camera moves ---------- */
  let camAnim = null;
  function flyTo(def, dur) {
    if (camAnim) camAnim.dead = true;
    const p0 = camera.position.clone(), t0 = controls.target.clone();
    const p1 = new THREE.Vector3(...def.pos), t1 = new THREE.Vector3(...def.tgt);
    const tw = animate({
      dur: REDUCE ? 0.01 : (dur || 1.6),
      ease: easeInOutCubic,
      update(k) {
        if (tw.dead) return;
        camera.position.lerpVectors(p0, p1, k);
        controls.target.lerpVectors(t0, t1, k);
      }
    });
    camAnim = tw;
    armIdle();
  }

  /* ---------- x-ray ---------- */
  let sweepMesh = null;
  function ensureSweep() {
    if (sweepMesh) return sweepMesh;
    sweepMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(3.0, 1.7),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(2.5, 0.3, 0.5), transparent: true, opacity: 0, toneMapped: false, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide })
    );
    sweepMesh.position.y = 0.85;
    carGroup.add(sweepMesh);
    return sweepMesh;
  }
  let xrayAnim = false;
  let xrayQueued = null;
  function setXray(on) {
    if (!ghostCar) return;
    if (xrayAnim) { xrayQueued = on; return; }
    if (on === state.xray) return;
    state.xray = on;
    syncChips();
    xrayAnim = true;
    const sweep = ensureSweep();
    ghostCar.visible = true;
    origMatMeshes.forEach(([, mt]) => { mt.clippingPlanes = [clipPlane]; mt.clipShadows = true; });
    ghostMats.forEach(m => { m.uniforms.uClipOn.value = 1; m.uniforms.uAlpha.value = 1; });
    const Z0 = -2.7, Z1 = 2.7;
    const from = on ? Z0 : Z1, to = on ? Z1 : Z0;
    sweep.material.opacity = 0.5;
    animate({
      dur: REDUCE ? 0.01 : 1.05,
      ease: easeInOutCubic,
      update(k) {
        const z = from + (to - from) * k;
        // original kept where z > clipZ ⇒ plane z - clipZ >= 0 ⇒ constant = -clipZ
        clipPlane.constant = -z;
        ghostMats.forEach(m => { m.uniforms.uClipZ.value = z; });
        sweep.position.z = z;
        sweep.material.opacity = 0.5 * Math.sin(Math.PI * Math.min(1, k));
      },
      done() {
        sweep.material.opacity = 0;
        xrayAnim = false;
        if (xrayQueued !== null) {
          const q = xrayQueued; xrayQueued = null;
          if (q !== state.xray) { setTimeout(() => setXray(q), 30); }
        }
        if (on) {
          // fully ghost: hide originals entirely
          origMatMeshes.forEach(([msh]) => { msh.visible = false; });
          ghostMats.forEach(m => { m.uniforms.uClipOn.value = 0; });
        } else {
          ghostCar.visible = false;
          origMatMeshes.forEach(([msh, mt]) => { msh.visible = true; mt.clippingPlanes = null; });
          ghostMats.forEach(m => { m.uniforms.uClipOn.value = 0; m.uniforms.uAlpha.value = 0; });
        }
      }
    });
    if (on) origMatMeshes.forEach(([msh]) => { msh.visible = true; });
  }

  /* ---------- views ---------- */
  function setView(v) {
    state.view = v;
    syncChips();
    const wasLift = state.lifted > 0.5;
    if (v === 'lift') {
      animate({ dur: REDUCE ? 0.01 : 1.7, ease: easeInOutCubic, update(k) { state.lifted = k; applyLift(); } });
      flyTo(TUNE.camLift, 1.9);
      if (!state.xray) setTimeout(() => { if (state.view === 'lift') setXray(true); }, REDUCE ? 0 : 700);
    } else {
      if (wasLift) animate({
        dur: REDUCE ? 0.01 : 1.4, ease: easeInOutCubic,
        update(k) { state.lifted = 1 - k; applyLift(); },
        done() {
          if (REDUCE) return;
          animate({ dur: 0.8, ease: (t) => t, update(k) { carShell.position.y = 0.03 * Math.exp(-5 * k) * Math.sin(k * 21); }, done() { carShell.position.y = 0; } });
        }
      });
      if (state.xray && v !== 'lift') setXray(false);
      flyTo(v === 'rear' ? TUNE.camRear : TUNE.camShowroom, 1.6);
    }
  }
  function applyLift() {
    carGroup.position.y = state.lifted * TUNE.liftHeight;
    underGlow.intensity = state.lifted * 30;
    blob.material.opacity = 0.75 * (1 - state.lifted * 0.75);
    blob.scale.setScalar(1 + state.lifted * 0.25);
  }

  /* ---------- tips / paint ---------- */
  const TIP_STYLES = {
    'cf-steel': { sleeve: 'carbon', inner: 'tipSteel', label: 'CARBON / STAINLESS' },
    'cf-black': { sleeve: 'carbon', inner: 'tipBlack', label: 'CARBON / GLOSS BLACK' },
    'cf-gold': { sleeve: 'carbon', inner: 'tipGold', label: 'CARBON / GOLD' },
    'steel': { sleeve: 'tipSteelBrushed', inner: 'tipSteel', label: 'FULL STAINLESS' }
  };
  function applyTip(key, fromVariant) {
    if (!TIP_STYLES[key] || TIP_COUNT === 0) return;
    state.tip = key;
    const st = TIP_STYLES[key];
    tips.forEach(t => {
      t.sleeve.material = MAT[st.sleeve];
      t.inner.material = MAT[st.inner];
      t.lip.material = st.sleeve === 'carbon' ? MAT[st.inner] : MAT.tipSteel;
      t.sleeveEdge.material = st.sleeve === 'carbon' ? MAT.carbonEdge : MAT[st.sleeve];
      t.soot.material = st.sleeve === 'carbon' ? MAT.boreSoot : MAT.boreSteel;
    });
    syncChips();
    if (!fromVariant) selectVariant(key);
  }

  const PAINTS = [
    { key: 'yas', hex: 0x2a5fa8, label: 'YAS MARINA' },
    { key: 'alpine', hex: 0xf2f3f2, label: 'ALPINE WHITE' },
    { key: 'sapphire', hex: 0x0b0c10, label: 'BLACK SAPPHIRE' },
    { key: 'austin', hex: 0xd2b13c, label: 'AUSTIN YELLOW' },
    { key: 'sakhir', hex: 0xc2601e, label: 'SAKHIR ORANGE' },
    { key: 'qhce', hex: 0xc8102e, label: 'QHCE RED' }
  ];
  function applyPaint(key) {
    const p = PAINTS.find(x => x.key === key);
    if (!p) return;
    state.paint = key;
    bodyMats.forEach((mt, i) => {
      if (!mt || !mt.color) return;
      const target = p.hex === null ? factoryColors[i] : new THREE.Color(p.hex);
      if (!target) return;
      const c0 = mt.color.clone();
      animate({ dur: REDUCE ? 0.01 : 0.5, update(k) { mt.color.lerpColors(c0, target, k); } });
    });
    syncChips();
  }

  /* ---------- variant sync (Horizon buy box) ---------- */
  function variantKeyFromText(txt) {
    const t = (txt || '').toLowerCase();
    if (t.includes('gold')) return 'cf-gold';
    if (t.includes('black')) return 'cf-black';
    if (t.includes('full') || (!t.includes('carbon') && t.includes('stainless'))) return 'steel';
    if (t.includes('carbon')) return 'cf-steel';
    return null;
  }
  const VARIANT_VALUES = (CFG.optionValues || []);
  function selectVariant(key) {
    try {
      const inputs = document.querySelectorAll('variant-picker input[type="radio"], fieldset input[type="radio"]');
      for (const inp of inputs) {
        if (variantKeyFromText(inp.value) === key && VARIANT_VALUES.some(v => v === inp.value)) {
          if (!inp.checked) inp.click();
          return;
        }
      }
    } catch (e) {}
  }
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t || t.type !== 'radio') return;
    if (!VARIANT_VALUES.some(v => v === t.value)) return;
    const key = variantKeyFromText(t.value);
    if (key && key !== state.tip) applyTip(key, true);
  });

  /* ---------- hotspots ---------- */
  const HOTSPOTS = [
    { pos: [0.37, () => EX.tipY, EX.tipZ], title: 'QUAD CARBON TIPS', body: 'Four outlets, laser-etched QHCE inside the lip. Carbon sleeve or full stainless — pick your finish in the configurator.' },
    { pos: [0.52, () => EX.muffY + 0.09, EX.muffZ], title: 'ELECTRONIC VALVES', body: 'Butterfly valves on both banks. Remote, key-fob and cabin switch included — whisper closed, wide open when it matters.' },
    { pos: [-0.18, () => EX.pipeY + 0.03, 0.35], title: 'T304 X-PIPE', body: 'Mandrel-bent T304 crossover mid-pipes, TIG-welded at every joint, OEM flange fitment. Bolt-on — no tune required.' }
  ];
  const dots = [];
  if (dotLayer) {
    HOTSPOTS.forEach((h, i) => {
      const d = document.createElement('button');
      d.type = 'button';
      d.className = 'qhce3d-dot';
      d.setAttribute('aria-label', h.title);
      d.innerHTML = '<i></i>';
      d.addEventListener('click', () => showCard(i));
      dotLayer.appendChild(d);
      dots.push(d);
    });
  }
  function showCard(i) {
    const h = HOTSPOTS[i];
    if (!infoCard) return;
    infoCard.querySelector('.qhce3d-card-title').textContent = h.title;
    infoCard.querySelector('.qhce3d-card-body').textContent = h.body;
    infoCard.classList.add('qhce3d-open');
  }
  if (infoCard) infoCard.querySelector('.qhce3d-card-x').addEventListener('click', () => infoCard.classList.remove('qhce3d-open'));

  const v3 = new THREE.Vector3();
  function projectDots() {
    const show = state.xray || state.view === 'lift';
    dots.forEach((d, i) => {
      if (!show || !state.loaded) { d.style.opacity = 0; d.style.pointerEvents = 'none'; return; }
      const h = HOTSPOTS[i];
      v3.set(h.pos[0], typeof h.pos[1] === 'function' ? h.pos[1]() : h.pos[1], h.pos[2]);
      carGroup.localToWorld(v3);
      v3.project(camera);
      if (v3.z > 1) { d.style.opacity = 0; d.style.pointerEvents = 'none'; return; }
      const x = (v3.x * 0.5 + 0.5) * stage.clientWidth;
      const y = (-v3.y * 0.5 + 0.5) * stage.clientHeight;
      d.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
      d.style.opacity = 1;
      d.style.pointerEvents = 'auto';
    });
  }

  /* ---------- HUD wiring ---------- */
  function syncChips() {
    root.querySelectorAll('[data-q3d-view]').forEach(b => b.classList.toggle('qhce3d-on', b.dataset.q3dView === state.view));
    root.querySelectorAll('[data-q3d-tip]').forEach(b => b.classList.toggle('qhce3d-on', b.dataset.q3dTip === state.tip));
    root.querySelectorAll('[data-q3d-paint]').forEach(b => b.classList.toggle('qhce3d-on', b.dataset.q3dPaint === state.paint));
    const xr = $('[data-q3d-xray]');
    if (xr) { xr.classList.toggle('qhce3d-on', state.xray); xr.setAttribute('aria-pressed', state.xray); }
    const vv = $('[data-q3d-valves]');
    if (vv) {
      vv.classList.toggle('qhce3d-on', state.valves);
      vv.setAttribute('aria-pressed', state.valves);
      const t = vv.querySelector('.qhce3d-valvetxt');
      if (t) t.textContent = state.valves ? 'VALVES OPEN' : 'VALVES CLOSED';
    }
    root.classList.toggle('qhce3d-valves-open', state.valves);
  }
  root.querySelectorAll('[data-q3d-view]').forEach(b => b.addEventListener('click', () => { dismissHint(); setView(b.dataset.q3dView); }));
  root.querySelectorAll('[data-q3d-tip]').forEach(b => b.addEventListener('click', () => applyTip(b.dataset.q3dTip)));
  root.querySelectorAll('[data-q3d-paint]').forEach(b => b.addEventListener('click', () => applyPaint(b.dataset.q3dPaint)));
  const xrBtn = $('[data-q3d-xray]');
  if (xrBtn) xrBtn.addEventListener('click', () => { dismissHint(); setXray(!state.xray); });
  const vBtn = $('[data-q3d-valves]');
  if (vBtn) vBtn.addEventListener('click', () => { state.valves = !state.valves; syncChips(); });
  const revBtn = $('[data-q3d-rev]');
  if (revBtn) {
    const down = (e) => { if (e.cancelable) e.preventDefault(); state.throttle = 1; revBtn.classList.add('qhce3d-hold'); dismissHint(); };
    const up = () => {
      if (state.throttle && state.rpm > 5200) scheduleCrackle();
      state.throttle = 0; revBtn.classList.remove('qhce3d-hold');
    };
    revBtn.addEventListener('pointerdown', down);
    window.addEventListener('pointerup', up);
    revBtn.addEventListener('pointercancel', up);
    revBtn.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); state.throttle = 1; } });
    revBtn.addEventListener('keyup', (e) => { if (e.key === ' ' || e.key === 'Enter') up(); });
  }

  /* tip fitment: slide the barrels over the pipes like the real slip-fit hardware */
  function applyFit(mm) {
    state.fit = Math.max(-25, Math.min(35, mm | 0));
    tips.forEach(t => { t.group.position.z = EX.tipZ + state.fit / 1000; });
    const lab = $('.qhce3d-fit-val');
    if (lab) lab.textContent = state.fit === 0 ? 'FACTORY' : (state.fit > 0 ? '+' + state.fit + ' MM OUT' : (-state.fit) + ' MM IN');
  }
  const fitInput = $('[data-q3d-fit]');
  if (fitInput) fitInput.addEventListener('input', () => applyFit(+fitInput.value));
  if (TIP_COUNT === 0) {
    const tipBtn = $('[data-q3d-tip]');
    if (tipBtn) { const cl = tipBtn.closest('.qhce3d-cluster'); if (cl) cl.style.display = 'none'; }
  }

  /* flame tune flash tool */
  const tuner = $('[data-q3d-tuner]');
  const tunerChip = $('[data-q3d-tunerchip]');
  if (tunerChip && tuner) {
    tunerChip.addEventListener('click', () => {
      tuner.classList.toggle('qhce3d-open');
      tunerChip.classList.toggle('qhce3d-on', tuner.classList.contains('qhce3d-open'));
    });
    const x = tuner.querySelector('.qhce3d-tuner-x');
    if (x) x.addEventListener('click', () => { tuner.classList.remove('qhce3d-open'); tunerChip.classList.remove('qhce3d-on'); });
  }
  const flamesBtn = $('[data-q3d-flames]');
  if (flamesBtn) flamesBtn.addEventListener('click', () => {
    state.flames = !state.flames;
    flamesBtn.classList.toggle('qhce3d-on', state.flames);
    flamesBtn.setAttribute('aria-pressed', state.flames);
    if (state.flames && !state.valves) { state.valves = true; syncChips(); }  // flames need open valves
  });
  const BURBLE_ZONES = [[8, 'STOCK'], [45, 'STAGE 1'], [78, 'STAGE 2'], [101, 'UNHINGED']];
  function burbleLabel() {
    const lab = $('.qhce3d-burble-val');
    if (!lab) return;
    lab.textContent = BURBLE_ZONES.find(z => state.burble < z[0])[1];
  }
  const burbleInput = $('[data-q3d-burble]');
  if (burbleInput) burbleInput.addEventListener('input', () => { state.burble = +burbleInput.value; burbleLabel(); });
  burbleLabel();

  const sndBtn = $('[data-q3d-sound]');
  if (sndBtn) sndBtn.addEventListener('click', () => {
    state.sound = !state.sound;
    if (state.sound) {
      engineAudio.start();
      // throttle blip so you hear it immediately
      state.throttle = 1;
      setTimeout(() => { state.throttle = 0; if (state.rpm > 5200) scheduleCrackle(); }, 500);
    } else engineAudio.stop();
    sndBtn.classList.toggle('qhce3d-on', state.sound);
    sndBtn.setAttribute('aria-pressed', state.sound);
    const txt = sndBtn.querySelector('.qhce3d-soundtxt');
    if (txt) txt.textContent = state.sound ? 'SOUND ON' : 'SOUND OFF';
  });

  /* EQ bars */
  let bars = [];
  if (barsEl) {
    let h = '';
    for (let i = 0; i < 18; i++) h += '<i></i>';
    barsEl.innerHTML = h;
    bars = [...barsEl.querySelectorAll('i')];
  }

  /* ---------- crackle scheduling ---------- */
  const crackleQueue = [];
  function scheduleCrackle() {
    const b = state.burble / 100;
    let n;
    if (!state.valves) n = 1 + Math.floor(Math.random() * 2);          // muffled, valves shut
    else if (state.burble < 8) n = Math.random() < 0.4 ? 1 : 0;        // STOCK map: barely a pop
    else n = Math.round(2 + b * 9 + Math.random() * 2);
    let t = 0;
    for (let i = 0; i < n; i++) {
      t += 0.045 + Math.random() * (0.16 - b * 0.06);
      crackleQueue.push(clockNow + t);
    }
  }

  /* ---------- intro ---------- */
  function intro() {
    state.loaded = true;
    if (loaderEl) loaderEl.classList.add('qhce3d-done');
    if (hudEl) hudEl.classList.add('qhce3d-hud-in');
    syncChips();
    applyTip(state.tip, true);
    resize();
    // lights on, staggered with flicker
    const seq = [[key, LIGHT_FULL.key, 0], [rimRed, LIGHT_FULL.rimRed, 0.35], [rimCool, LIGHT_FULL.rimCool, 0.6]];
    seq.forEach(([light, full, delay]) => {
      setTimeout(() => {
        animate({
          dur: REDUCE ? 0.01 : 0.9,
          update(k) {
            const flick = REDUCE ? 1 : (k < 0.7 ? (Math.random() > 0.4 ? k : k * 0.3) : k);
            light.intensity = full * flick;
          },
          done() { light.intensity = full; }
        });
      }, REDUCE ? 0 : delay * 1000);
    });
    if (REDUCE) {
      camera.position.set(...TUNE.camShowroom.pos);
      controls.target.set(...TUNE.camShowroom.tgt);
    } else {
      flyTo(TUNE.camShowroom, 2.6);
    }
  }

  /* ---------- adaptive quality ---------- */
  let frames = 0, fpsAccum = 0, degraded = 0;
  function adapt(dt) {
    if (degraded > 1 || frames > 900) return;
    frames++;
    fpsAccum += dt;
    if (frames % 240 === 0) {
      const fps = 240 / fpsAccum;
      fpsAccum = 0;
      if (fps < 26 && degraded === 0) { degraded = 1; if (mirror) mirror.visible = false; if (bloomPass) bloomPass.enabled = false; }
      else if (fps < 20 && degraded === 1) { degraded = 2; renderer.setPixelRatio(1); if (dust) dust.visible = false; resize(); }
    }
  }

  /* ---------- main loop ---------- */
  let last = performance.now();
  const camShakeOff = new THREE.Vector3();
  function loop(nowMs) {
    requestAnimationFrame(loop);
    const now = nowMs / 1000;
    clockNow = now;
    const dt = Math.min(0.05, (nowMs - last) / 1000);
    last = nowMs;

    stepTweens(now);

    // rpm physics
    const idle = IDLE + (state.throttle ? 0 : (REDUCE ? 0 : Math.sin(now * 1.9) * 16));
    const tgt = state.throttle ? MAXR - 140 : idle;
    const kk = tgt > state.rpm ? 4.4 : 3.8;
    state.rpm += (tgt - state.rpm) * Math.min(1, kk * dt);
    if (rpmNum) rpmNum.textContent = Math.round(state.rpm / 10) * 10;
    root.classList.toggle('qhce3d-redline', state.rpm >= REDLINE);

    const rf = Math.max(0, (state.rpm - IDLE) / (MAXR - IDLE));

    // crackle events
    for (let i = crackleQueue.length - 1; i >= 0; i--) {
      if (now >= crackleQueue[i]) {
        crackleQueue.splice(i, 1);
        const b = state.burble / 100;
        const t1 = tips[Math.floor(Math.random() * tips.length)];
        const flame = state.flames && state.valves && (Math.random() < 0.4 + b * 0.4);
        popAt(t1, state.valves, flame);
        if (flame) {
          spawnFlame(t1, state.valves ? 1 : 0.5);
          if (Math.random() < b * 0.7) spawnFlame(tips[Math.floor(Math.random() * tips.length)], 0.6);
        }
        if (state.valves && Math.random() > 0.5) popAt(tips[Math.floor(Math.random() * tips.length)], false, false);
        state.shake = Math.min(1, state.shake + 0.5 + (flame ? 0.25 : 0));
      }
    }
    if (state.throttle && state.rpm > REDLINE - 300 && state.valves && Math.random() < dt * 3.5) popAt(tips[Math.floor(Math.random() * tips.length)], false);

    // sprites
    sprites.forEach(s => {
      if (!s.sp.visible) return;
      s.life -= dt;
      if (s.life <= 0) { s.sp.visible = false; s.sp.material.opacity = 0; return; }
      const k = s.life / s.max;
      s.sp.material.opacity = k * (s.big ? 0.95 : 0.6);
      s.sp.scale.setScalar((1 - k * 0.5) * (s.big ? 0.34 : 0.18));
    });

    // exhaust heat glow (valves + rpm)
    const heat = Math.max(0, rf - 0.02) * (state.valves ? 2.3 : 0.95);
    tips.forEach(t => {
      t.glowMat.color.setRGB(0.004 + heat * 1.9, 0.001 + heat * 0.2, 0.001 + heat * 0.12);
    });
    heatFlash = Math.max(0, heatFlash - dt * 3.4);
    heatLight.color.setHex(heatFlash > 0.25 ? 0xff7a18 : 0xff2d14);
    heatLight.intensity = Math.max(0, heat - 0.35) * 2.4 + heatFlash * 30;
    heatLight.position.y = 0.34 + carGroup.position.y;
    tips.forEach(t => {
      const f = t.flame;
      if (!f || f.life <= 0) { if (f && f.outer.visible) { f.outer.visible = f.core.visible = false; } return; }
      f.life -= dt;
      const k = Math.max(0, f.life / f.max);
      const flick = 0.75 + Math.random() * 0.35;
      f.outer.visible = f.core.visible = true;
      f.outer.material.opacity = k * 0.85 * flick;
      f.core.material.opacity = k * flick;
      f.outer.scale.set(0.8 + Math.random() * 0.3, (0.65 + f.power * 0.7) * (0.75 + Math.random() * 0.45), 0.8 + Math.random() * 0.3);
      f.core.scale.set(0.8 + Math.random() * 0.3, (0.6 + f.power * 0.6) * (0.75 + Math.random() * 0.45), 0.8 + Math.random() * 0.3);
      f.outer.rotation.y += dt * 22; f.core.rotation.y -= dt * 27;
    });
    valveLeds.forEach(m => m.color.setRGB(state.valves ? 1.6 : 0.18, state.valves ? 0.12 : 0.02, state.valves ? 0.16 : 0.02));

    // flow overlays: visible in x-ray, speed by rpm+valves
    const flowVis = state.xray ? (state.valves ? 0.5 : 0.22) : 0;
    flowMats.forEach(m => {
      m.opacity += (flowVis - m.opacity) * Math.min(1, 6 * dt);
      m.userData.tex.offset.x -= dt * (0.25 + rf * 2.2) * (state.valves ? 1.6 : 1);
    });

    // camera shake + fov kick
    state.shake = Math.max(0, state.shake - dt * 2.4);
    const shakeAmp = REDUCE ? 0 : (rf * rf * 0.011 * (state.valves ? 1.5 : 1) + state.shake * 0.012);
    if (shakeAmp > 0.0002) {
      camShakeOff.set(
        Math.sin(now * 61) * shakeAmp + Math.sin(now * 23.7) * shakeAmp * 0.6,
        Math.cos(now * 47) * shakeAmp * 0.8,
        Math.sin(now * 53.3) * shakeAmp * 0.5
      );
    } else camShakeOff.set(0, 0, 0);
    const fovTarget = BASE_FOV + rf * 2.6;
    if (Math.abs(camera.fov - fovTarget) > 0.01) { camera.fov += (fovTarget - camera.fov) * Math.min(1, 5 * dt); camera.updateProjectionMatrix(); }

    // dust drift
    if (dust && dust.visible && !REDUCE) {
      const p = dust.geometry.attributes.position, seed = dust.userData.seed;
      for (let i = 0; i < p.count; i++) {
        p.array[i * 3 + 1] += dt * 0.045;
        p.array[i * 3] += Math.sin(now * 0.3 + seed[i]) * dt * 0.02;
        if (p.array[i * 3 + 1] > 3.6) p.array[i * 3 + 1] = 0.05;
      }
      p.needsUpdate = true;
    }

    // EQ bars
    if (bars.length) {
      const amp = state.valves ? 1 : 0.4;
      for (let i = 0; i < bars.length; i++) {
        const center = 1 - Math.abs(i - (bars.length - 1) / 2) / ((bars.length - 1) / 2);
        const wob = REDUCE ? 0.5 : 0.5 + 0.5 * Math.sin(now * 6.6 + i * 0.7);
        let hgt = 0.08 + (0.14 + rf * 0.8) * amp * (0.45 + 0.55 * center) * wob;
        bars[i].style.transform = `scaleY(${Math.min(1, hgt).toFixed(3)})`;
      }
    }

    controls.update();
    camera.position.add(camShakeOff);
    projectDots();

    if (composer && bloomPass && bloomPass.enabled) composer.render();
    else renderer.render(scene, camera);
    camera.position.sub(camShakeOff);

    adapt(dt);
  }

  /* ---------- boot ---------- */
  if (CFG.title) { const el = $('.qhce3d-title'); if (el) el.textContent = CFG.title; }
  if (TUNE.subline) { const el = $('.qhce3d-subline'); if (el) el.textContent = TUNE.subline; }

  resize();
  setupPost();
  requestAnimationFrame(loop);   // render behind the loader immediately
  const [gotModel] = await Promise.all([loadCar(), upgradeEnv()]);
  intro();
  if (gotModel) applyPaint(state.paint);

  // debug/tuning hook (harmless in production)
  window.__QHCE3D__ = { THREE, scene, camera, controls, renderer, get bloom() { return bloomPass; }, state, TUNE, exhaust, carShell, carGroup, tips, MAT, setView, setXray, applyTip, applyPaint, flyTo, engineAudio };
})();
